import React, { useState, useEffect } from 'react';
import { fetchAPI } from '../../utils/api';
import {
    FiCalendar, FiClock, FiCheckCircle, FiRefreshCw,
    FiUser, FiBook, FiInfo
} from 'react-icons/fi';
import { motion } from 'framer-motion';

export default function StudentAttendance() {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadAttendance();
    }, []);

    const loadAttendance = async () => {
        try {
            setLoading(true);
            const data = await fetchAPI('/student/get_attendance.php');
            setHistory(data);
            setError(null);
        } catch (err) {
            console.error("Failed to load attendance", err);
            setError("Could not load your attendance history. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            {/* Header Section */}
            <div className="bg-white rounded-xl p-5 md:p-6 shadow-sm border border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-md">
                                <FiCalendar size={18} />
                            </div>
                            <h1 className="text-xl font-bold text-gray-900 tracking-tight">My Attendance</h1>
                        </div>
                        <p className="text-gray-400 font-medium text-[10px] uppercase tracking-wider ml-1">
                            Track your campus history
                        </p>
                    </div>

                    <button
                        onClick={loadAttendance}
                        disabled={loading}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg font-bold uppercase tracking-wider text-[9px] transition-all border border-gray-200 disabled:opacity-50"
                    >
                        <FiRefreshCw className={loading ? 'animate-spin' : ''} size={12} />
                        Refresh History
                    </button>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { label: 'Total Present', value: history.length, icon: <FiCheckCircle />, color: 'text-green-600', bg: 'bg-green-50' },
                    { label: 'Classes Attended', value: new Set(history.map(h => h.class_id)).size, icon: <FiBook />, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Last Activity', value: history[0] ? new Date(history[0].marked_at).toLocaleDateString() : 'N/A', icon: <FiClock />, color: 'text-purple-600', bg: 'bg-purple-50' },
                ].map((stat, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4"
                    >
                        <div className={`w-12 h-12 ${stat.bg} ${stat.color} rounded-lg flex items-center justify-center text-xl shadow-inner`}>
                            {stat.icon}
                        </div>
                        <div>
                            <p className="text-[9px] font-bold uppercase text-gray-400 tracking-widest mb-0.5">{stat.label}</p>
                            <h3 className="text-lg font-bold text-gray-900 tracking-tight">{stat.value}</h3>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Attendance List */}
            <div className="bg-white rounded-xl p-5 md:p-6 shadow-sm border border-gray-100 min-h-[400px]">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400 border border-gray-100">
                        <FiBook size={16} />
                    </div>
                    <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Recent History</h2>
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-20 bg-gray-50 rounded-2xl animate-pulse"></div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="text-center py-20 bg-red-50/50 rounded-[2rem] border border-dashed border-red-200">
                        <FiInfo size={40} className="mx-auto text-red-300 mb-4" />
                        <p className="text-red-500 font-bold uppercase tracking-widest text-xs">{error}</p>
                    </div>
                ) : history.length === 0 ? (
                    <div className="text-center py-20 bg-gray-50/50 rounded-[2rem] border border-dashed border-gray-200">
                        <FiCalendar size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">No attendance records found</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {history.map((record, idx) => (
                            <motion.div
                                key={record.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                className="group flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50/70 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all duration-200 gap-4"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-blue-600 text-sm font-bold shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-all duration-200">
                                        {record.class_grade}
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="font-bold text-gray-900 uppercase text-sm truncate">{record.class_name}</h4>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-400 font-medium text-[9px] uppercase tracking-wider mt-0.5">
                                            <span className="flex items-center gap-1.5"><FiCalendar size={10} className="text-blue-500/50" /> {new Date(record.marked_at).toLocaleDateString()}</span>
                                            <span className="flex items-center gap-1.5"><FiClock size={10} className="text-blue-500/50" /> {new Date(record.marked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 self-end sm:self-center">
                                    <div className="text-right mr-2 hidden md:block">
                                        <p className="text-[8px] font-bold text-gray-400 uppercase tracking-wider">Teacher</p>
                                        <p className="text-[10px] font-bold text-gray-500">{record.marker_name || "System"}</p>
                                    </div>
                                    <div className="px-4 py-1.5 bg-green-500 text-white rounded-md text-[9px] font-bold uppercase tracking-wider">
                                        {record.status}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Info Footer */}
            <div className="bg-gray-900 rounded-xl p-4 text-gray-500 text-center">
                <p className="text-[8px] font-bold uppercase tracking-[0.2em]">Sudesh Maths Academic Portal</p>
                <p className="text-xs">Your attendance is marked manually or via QR code at the campus.</p>
            </div>
        </div>
    );
}
